/**
 * ShowingsCalendar — Week/month calendar view for showing appointments.
 *
 * Receives `appointments` as a prop (does NOT fetch data).
 * Colour-coded by outcome. Clicking an appointment block calls onAppointmentClick.
 * Clicking an empty time cell calls onEmptySlotClick with a Date.
 * Navigation buttons allow moving prev/next week or prev/next month.
 */

import { useState } from 'react';
import type { ShowingAppointment, ShowingOutcome } from '../../lib/leasing.api.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ShowingsCalendarProps {
  appointments: ShowingAppointment[];
  onAppointmentClick: (appointmentId: string) => void;
  onEmptySlotClick: (date: Date) => void;
  slotMinutes?: number; // default 30 — time band granularity
  /** IANA timezone for the listing unit. All times are displayed in this zone. */
  timezone?: string;
}

type CalendarView = 'week' | 'month';

// ---------------------------------------------------------------------------
// Date helpers — always use local date getters to avoid UTC cross-midnight bugs
// ---------------------------------------------------------------------------

function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Derive a YYYY-MM-DD string from local date components.
 * NEVER slice ISO strings — that would use UTC, causing cross-midnight grouping bugs.
 */
function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDayHeader(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
}

function formatWeekRange(monday: Date): string {
  const sunday = addDays(monday, 6);
  const start = monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const end = sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${start} – ${end}`;
}

function formatMonthYear(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatTimeInTz(date: Date, tz: string): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz });
}

function formatTimeLabel(hours: number, minutes: number): string {
  // Row labels come from band keys which are already in listing-tz HH:MM; format as UTC to avoid re-conversion
  const ref = new Date(Date.UTC(2000, 0, 1, hours, minutes, 0));
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' }).format(ref);
}

function getFirstDayOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getLastDayOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

// ---------------------------------------------------------------------------
// Outcome colour classes (matches phase doc)
// ---------------------------------------------------------------------------

// WARN-6: Aligned with OUTCOME_COLORS in ListingDetailPage so the same outcome
// reads the same colour in both the list-view badge and the calendar block.
const OUTCOME_CALENDAR_COLORS: Record<ShowingOutcome, string> = {
  scheduled: 'bg-success/15 text-success',
  completed: 'bg-muted text-muted-foreground',
  cancelled: 'bg-destructive/15 text-destructive',
  rescheduled: 'bg-warning/15 text-warning',
};

const OUTCOME_LABELS: Record<ShowingOutcome, string> = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled',
};

// ---------------------------------------------------------------------------
// Appointment grouping by LOCAL date
// ---------------------------------------------------------------------------

type AppointmentsByDate = Map<string, ShowingAppointment[]>;

function groupAppointmentsByListingDate(appointments: ShowingAppointment[], tz: string): AppointmentsByDate {
  const formatter = new Intl.DateTimeFormat('en-CA', { timeZone: tz });
  const map = new Map<string, ShowingAppointment[]>();
  for (const appt of appointments) {
    const dateStr = formatter.format(new Date(appt.scheduledFor));
    const arr = map.get(dateStr) ?? [];
    arr.push(appt);
    map.set(dateStr, arr);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Time band utilities
// ---------------------------------------------------------------------------

interface TimeBand {
  hours: number;
  minutes: number;
  key: string; // "HH:MM"
}

/**
 * Find which time band an appointment belongs to (rounded down to nearest band), in listing timezone.
 */
function getBandKeyForAppointment(appt: ShowingAppointment, slotMinutes: number, tz: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(appt.scheduledFor));
  const partMap: Record<string, string> = {};
  for (const p of parts) partMap[p.type] = p.value;
  const h = parseInt(partMap['hour'] ?? '0', 10);
  const m = parseInt(partMap['minute'] ?? '0', 10);
  const totalMins = h * 60 + m;
  const bandMins = Math.floor(totalMins / slotMinutes) * slotMinutes;
  const bandH = Math.floor(bandMins / 60);
  const bandM = bandMins % 60;
  return `${String(bandH).padStart(2, '0')}:${String(bandM).padStart(2, '0')}`;
}

/**
 * Collect all band keys that actually have appointments in the given week dates.
 */
function collectOccupiedBandKeys(
  apptsByDate: AppointmentsByDate,
  weekDates: string[],
  slotMinutes: number,
  tz: string
): string[] {
  const keys = new Set<string>();
  for (const dateStr of weekDates) {
    const appts = apptsByDate.get(dateStr) ?? [];
    for (const appt of appts) {
      keys.add(getBandKeyForAppointment(appt, slotMinutes, tz));
    }
  }
  return Array.from(keys).sort();
}

// ---------------------------------------------------------------------------
// WeekView sub-component
// ---------------------------------------------------------------------------

interface WeekViewProps {
  monday: Date;
  apptsByDate: AppointmentsByDate;
  slotMinutes: number;
  onAppointmentClick: (appointmentId: string) => void;
  onEmptySlotClick: (date: Date) => void;
  timezone: string;
}

function WeekView({ monday, apptsByDate, slotMinutes, onAppointmentClick, onEmptySlotClick, timezone }: WeekViewProps) {
  const weekDates = Array.from({ length: 7 }, (_, i) => toLocalDateString(addDays(monday, i)));
  const occupiedBandKeys = collectOccupiedBandKeys(apptsByDate, weekDates, slotMinutes, timezone);

  // Show hours from 8am to 8pm by default, or all occupied bands
  const defaultBands: TimeBand[] = [];
  for (let h = 8; h < 20; h++) {
    for (let step = 0; step < 60; step += slotMinutes) {
      const key = `${String(h).padStart(2, '0')}:${String(step).padStart(2, '0')}`;
      defaultBands.push({ hours: h, minutes: step, key });
    }
  }

  // Build the set of band keys to display: default range + any occupied bands outside
  const allBandKeys = new Set<string>(defaultBands.map((b) => b.key));
  for (const k of occupiedBandKeys) {
    allBandKeys.add(k);
  }

  const allBands: TimeBand[] = Array.from(allBandKeys)
    .sort()
    .map((key) => {
      const [hStr, mStr] = key.split(':');
      return { hours: parseInt(hStr ?? '0', 10), minutes: parseInt(mStr ?? '0', 10), key };
    });

  return (
    <div data-testid="showings-calendar-week-view" className="relative overflow-x-auto">
      {/* WARN-8: scroll hint must be perceivable by screen-reader users — no aria-hidden */}
      <p className="sm:hidden text-xs text-muted-foreground px-4 pb-1 flex items-center gap-1">
        <span>Scroll to see all days</span>
        <span aria-hidden="true">→</span>
      </p>
      {/* Right-edge fade gradient indicating there is more content to scroll */}
      <div
        className="sm:hidden pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-card to-transparent z-10"
        aria-hidden="true"
      />
      <table className="w-full min-w-[560px] border-collapse text-xs">
        <thead>
          <tr>
            <th className="w-16 py-2 text-left text-muted-foreground font-normal pr-2" />
            {weekDates.map((dateStr, i) => {
              const d = addDays(monday, i);
              return (
                <th key={dateStr} className="py-2 text-center text-xs font-medium text-foreground px-1">
                  {formatDayHeader(d)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {allBands.map(({ hours, minutes, key }) => (
            <tr key={key} className="border-t border-border/30">
              <th
                scope="row"
                className="py-1 pr-2 text-right text-muted-foreground font-normal whitespace-nowrap align-top text-[10px] pt-1.5"
              >
                {minutes === 0 ? formatTimeLabel(hours, minutes) : ''}
              </th>
              {weekDates.map((dateStr, colIdx) => {
                const appts = (apptsByDate.get(dateStr) ?? []).filter(
                  (a) => getBandKeyForAppointment(a, slotMinutes, timezone) === key
                );

                if (appts.length > 0) {
                  return (
                    <td key={dateStr} className="px-0.5 py-0.5 align-top">
                      {appts.map((appt) => (
                        <button
                          key={appt.id}
                          type="button"
                          data-testid={`appointment-block-${appt.id}`}
                          onClick={() => onAppointmentClick(appt.id)}
                          aria-label={`${OUTCOME_LABELS[appt.outcome]} appointment at ${formatTimeInTz(new Date(appt.scheduledFor), timezone)}, ${appt.durationMinutes} min`}
                          className={[
                            'w-full text-left rounded px-1 py-1 text-[10px] font-medium leading-tight transition-colors',
                            'min-h-[44px] cursor-pointer hover:opacity-80 border border-transparent hover:border-current',
                            OUTCOME_CALENDAR_COLORS[appt.outcome],
                          ].join(' ')}
                        >
                          <span className="block font-semibold">
                            {formatTimeInTz(new Date(appt.scheduledFor), timezone)}
                          </span>
                          <span className="block opacity-80">{appt.durationMinutes}m</span>
                          <span className="block opacity-80">{OUTCOME_LABELS[appt.outcome]}</span>
                        </button>
                      ))}
                    </td>
                  );
                }

                // Empty cell — clicking opens schedule modal pre-filled
                const cellDate = new Date(addDays(monday, colIdx));
                cellDate.setHours(hours, minutes, 0, 0);

                return (
                  <td key={dateStr} className="px-0.5 py-0.5 align-top">
                    {/* FAIL-4: visible resting affordance — border + subtle bg so the
                        interactive region is discoverable without relying solely on hover */}
                    <button
                      type="button"
                      onClick={() => onEmptySlotClick(cellDate)}
                      aria-label={`Schedule showing on ${formatDayHeader(addDays(monday, colIdx))} at ${formatTimeLabel(hours, minutes)}`}
                      className="w-full min-h-[44px] rounded border border-border/30 bg-muted/10 hover:bg-muted/50 transition-colors cursor-pointer"
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MonthView sub-component
// ---------------------------------------------------------------------------

interface MonthViewProps {
  monthStart: Date;
  apptsByDate: AppointmentsByDate;
  onDayClick: (date: Date) => void;
}

function MonthView({ monthStart, apptsByDate, onDayClick }: MonthViewProps) {
  const firstDay = getFirstDayOfMonth(monthStart);
  const lastDay = getLastDayOfMonth(monthStart);

  // Start grid from Sunday of the first week
  const gridStart = new Date(firstDay);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());

  // End grid on Saturday of the last week
  const gridEnd = new Date(lastDay);
  const daysToSat = 6 - gridEnd.getDay();
  gridEnd.setDate(gridEnd.getDate() + daysToSat);

  const weeks: Date[][] = [];
  const cursor = new Date(gridStart);
  while (cursor <= gridEnd) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div data-testid="showings-calendar-month-view" className="p-2">
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {dayNames.map((d) => (
          <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">
            {d}
          </div>
        ))}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 gap-0.5">
          {week.map((day) => {
            // Use local date components for grouping — never slice ISO strings
            const dateStr = toLocalDateString(day);
            const appts = apptsByDate.get(dateStr) ?? [];
            const isCurrentMonth = day.getMonth() === monthStart.getMonth();
            const isPast = day < today;
            const isToday = toLocalDateString(day) === toLocalDateString(today);

            return (
              <button
                key={dateStr}
                type="button"
                onClick={() => onDayClick(day)}
                aria-label={`${day.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}${appts.length > 0 ? `, ${appts.length} showing${appts.length !== 1 ? 's' : ''}` : ''}`}
                className={[
                  'relative flex flex-col items-center justify-center rounded p-1 min-h-[44px] text-xs transition-colors',
                  isCurrentMonth ? 'text-foreground' : 'text-muted-foreground/40',
                  isPast ? 'opacity-60' : 'hover:bg-muted cursor-pointer',
                  isToday ? 'ring-2 ring-primary ring-offset-1' : '',
                  appts.length > 0 ? 'bg-info/10' : '',
                ].join(' ')}
              >
                <span className="font-medium">{day.getDate()}</span>
                {appts.length > 0 && (
                  <span className="text-[9px] text-info font-semibold leading-none mt-0.5">
                    {appts.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main ShowingsCalendar component
// ---------------------------------------------------------------------------

export function ShowingsCalendar({
  appointments,
  onAppointmentClick,
  onEmptySlotClick,
  slotMinutes = 30,
  timezone = 'America/New_York',
}: ShowingsCalendarProps) {
  // FAIL-2: default to month view on narrow viewports (< 640px) so the 7-column
  // week grid is not the first thing mobile users see — matches SlotCalendar pattern.
  const [viewMode, setViewMode] = useState<CalendarView>(() =>
    typeof window !== 'undefined' && window.innerWidth < 640 ? 'month' : 'week'
  );
  const [weekMonday, setWeekMonday] = useState<Date>(() => getMonday(new Date()));
  const [monthStart, setMonthStart] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const apptsByDate = groupAppointmentsByListingDate(appointments, timezone);

  // Navigation handlers
  function prevWeek() {
    setWeekMonday((m) => addDays(m, -7));
  }

  function nextWeek() {
    setWeekMonday((m) => addDays(m, 7));
  }

  function prevMonth() {
    setMonthStart((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  }

  function nextMonth() {
    setMonthStart((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  }

  function handleDayClickInMonth(day: Date) {
    setWeekMonday(getMonday(day));
    setViewMode('week');
  }

  const isWeek = viewMode === 'week';

  return (
    <div data-testid="showings-calendar" className="bg-card rounded-lg border border-border overflow-hidden">
      {/* FAIL-3: Header wraps to two rows on narrow viewports so the view-toggle
          button is never clipped off the right edge at 375px. On sm+ it stays
          as a single justify-between row (original layout). */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-3 border-b border-border bg-muted/30">
        {/* Navigation group — takes full width on xs, auto width on sm+ */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button
            type="button"
            data-testid="showings-calendar-prev"
            onClick={isWeek ? prevWeek : prevMonth}
            aria-label={isWeek ? 'Previous week' : 'Previous month'}
            className="p-2 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center shrink-0"
          >
            ‹
          </button>
          <span className="text-sm font-medium text-foreground text-center flex-1 truncate">
            {isWeek ? formatWeekRange(weekMonday) : formatMonthYear(monthStart)}
          </span>
          <button
            type="button"
            data-testid="showings-calendar-next"
            onClick={isWeek ? nextWeek : nextMonth}
            aria-label={isWeek ? 'Next week' : 'Next month'}
            className="p-2 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center shrink-0"
          >
            ›
          </button>
        </div>

        {/* View toggle — wraps below nav row on xs, stays inline on sm+ */}
        <div className="flex items-center gap-1 shrink-0">
          {isWeek ? (
            <button
              type="button"
              data-testid="showings-calendar-toggle-month"
              onClick={() => setViewMode('month')}
              aria-label="Switch to month view"
              className="text-xs px-3 py-2 rounded bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors min-h-[44px]"
            >
              Month
            </button>
          ) : (
            <button
              type="button"
              data-testid="showings-calendar-toggle-week"
              onClick={() => setViewMode('week')}
              aria-label="Switch to week view"
              className="text-xs px-3 py-2 rounded bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors min-h-[44px]"
            >
              Week
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="overflow-y-auto max-h-[480px]">
        {isWeek ? (
          <WeekView
            monday={weekMonday}
            apptsByDate={apptsByDate}
            slotMinutes={slotMinutes}
            onAppointmentClick={onAppointmentClick}
            onEmptySlotClick={onEmptySlotClick}
            timezone={timezone}
          />
        ) : (
          <MonthView
            monthStart={monthStart}
            apptsByDate={apptsByDate}
            onDayClick={handleDayClickInMonth}
          />
        )}
      </div>

      {/* Legend — colours match OUTCOME_CALENDAR_COLORS for visual consistency */}
      <div className="border-t border-border px-4 py-2 flex flex-wrap gap-3 text-[10px] text-muted-foreground bg-muted/10">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-sm bg-success/15" />
          Scheduled
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-sm bg-muted" />
          Completed
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-sm bg-destructive/15" />
          Cancelled
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-sm bg-warning/15" />
          Rescheduled
        </span>
      </div>
    </div>
  );
}

export default ShowingsCalendar;
